import React, { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, BookOpen, Pencil, Archive } from "lucide-react";
import PageHeader from "@/components/PageHeader";
import EmptyState from "@/components/EmptyState";
import { toast } from "sonner";

const TYPES = [
    { v: "asset", l: "Asset" }, { v: "liability", l: "Liability" },
    { v: "equity", l: "Equity" }, { v: "revenue", l: "Revenue" }, { v: "expense", l: "Expense" },
];

const TYPE_STYLES = {
    asset: "bg-secondary/10 text-secondary-dark",
    liability: "bg-coral/10 text-coral-dark",
    equity: "bg-ocean-50 text-ocean-700",
    revenue: "bg-emerald-50 text-emerald-700",
    expense: "bg-amber-50 text-amber-700",
};

const BLANK = { code: "", name: "", type: "expense", sub_type: "", description: "" };

export default function ChartOfAccounts() {
    const { currentOrg } = useAuth();
    const [items, setItems] = useState([]);
    const [open, setOpen] = useState(false);
    const [editing, setEditing] = useState(null);  // account object being edited
    const [form, setForm] = useState(BLANK);

    const load = async () => {
        const { data } = await api.get("/accounts");
        setItems(data.items || []);
    };
    useEffect(() => { if (currentOrg) load(); }, [currentOrg]);

    const openCreate = () => {
        setEditing(null);
        setForm(BLANK);
        setOpen(true);
    };

    const openEdit = (a) => {
        setEditing(a);
        setForm({
            code: a.code || "",
            name: a.name || "",
            type: a.type || "expense",
            sub_type: a.sub_type || "",
            description: a.description || "",
        });
        setOpen(true);
    };

    const submit = async () => {
        if (!form.code.trim() || !form.name.trim()) {
            toast.error("Code and name are required");
            return;
        }
        try {
            if (editing) {
                await api.patch(`/accounts/${editing.id}`, form);
                toast.success("Account updated");
            } else {
                await api.post("/accounts", form);
                toast.success("Account added");
            }
            setOpen(false); setEditing(null); setForm(BLANK); load();
        } catch (e) { toast.error(formatApiError(e)); }
    };

    const archive = async (a) => {
        if (!window.confirm(`Archive account "${a.code} — ${a.name}"? It will be hidden from this list but preserved for historical reports.`)) return;
        try {
            await api.delete(`/accounts/${a.id}`);
            toast.success("Account archived");
            load();
        } catch (e) { toast.error(formatApiError(e)); }
    };

    return (
        <div>
            <PageHeader
                subtitle="Accounting"
                title="Chart of accounts"
                action={
                    <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) { setEditing(null); setForm(BLANK); } }}>
                        <DialogTrigger asChild>
                            <Button onClick={openCreate} className="bg-ocean-700 text-white rounded-xl" data-testid="new-account-btn">
                                <Plus className="h-4 w-4 mr-1.5" /> New account
                            </Button>
                        </DialogTrigger>
                        <DialogContent>
                            <DialogHeader>
                                <DialogTitle data-testid="account-dialog-title">
                                    {editing ? `Edit account · ${editing.code}` : "New account"}
                                </DialogTitle>
                            </DialogHeader>
                            <div className="space-y-3">
                                <div className="grid grid-cols-2 gap-3">
                                    <div>
                                        <Label>Code</Label>
                                        <Input className="mt-1.5" value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value })} data-testid="account-code-input" />
                                    </div>
                                    <div>
                                        <Label>Type</Label>
                                        <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v })}>
                                            <SelectTrigger className="mt-1.5" data-testid="account-type-select"><SelectValue /></SelectTrigger>
                                            <SelectContent>{TYPES.map((t) => <SelectItem key={t.v} value={t.v}>{t.l}</SelectItem>)}</SelectContent>
                                        </Select>
                                    </div>
                                </div>
                                <div>
                                    <Label>Name</Label>
                                    <Input className="mt-1.5" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="account-name-input" />
                                </div>
                                <div>
                                    <Label>Sub-type</Label>
                                    <Input className="mt-1.5" value={form.sub_type} onChange={(e) => setForm({ ...form, sub_type: e.target.value })} data-testid="account-subtype-input" />
                                </div>
                                <div>
                                    <Label>Description</Label>
                                    <Input className="mt-1.5" value={form.description || ""} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="account-description-input" />
                                </div>
                            </div>
                            <DialogFooter>
                                <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
                                <Button onClick={submit} className="bg-ocean-700 text-white" data-testid="account-save-btn">
                                    {editing ? "Save changes" : "Save"}
                                </Button>
                            </DialogFooter>
                        </DialogContent>
                    </Dialog>
                }
            />
            {items.length === 0 ? (
                <EmptyState icon={BookOpen} title="No accounts yet" description="Setting up your first business seeds a full chart of accounts automatically." />
            ) : (
                <div className="rounded-3xl bg-white border border-sand-200 shadow-card overflow-hidden">
                    <table className="w-full text-sm">
                        <thead className="bg-sand-100 text-xs uppercase text-muted-foreground">
                            <tr>
                                <th className="text-left px-5 py-3">Code</th>
                                <th className="text-left px-5 py-3">Name</th>
                                <th className="text-left px-5 py-3">Type</th>
                                <th className="text-left px-5 py-3">Sub-type</th>
                                <th className="text-right px-5 py-3"></th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-sand-200">
                            {items.map((a) => (
                                <tr key={a.id} className="hover:bg-sand-50/50" data-testid={`account-row-${a.id}`}>
                                    <td className="px-5 py-3 font-mono">{a.code}</td>
                                    <td className="px-5 py-3 font-medium text-ocean-700">{a.name}</td>
                                    <td className="px-5 py-3"><span className={`inline-flex rounded-full px-2.5 py-0.5 text-xs font-medium ${TYPE_STYLES[a.type]}`}>{a.type}</span></td>
                                    <td className="px-5 py-3 text-muted-foreground">{a.sub_type || "—"}</td>
                                    <td className="px-5 py-3 text-right whitespace-nowrap">
                                        <Button variant="ghost" size="icon" onClick={() => openEdit(a)} title="Edit" data-testid={`edit-account-${a.id}`}>
                                            <Pencil className="h-4 w-4 text-muted-foreground" />
                                        </Button>
                                        <Button variant="ghost" size="icon" onClick={() => archive(a)} title="Archive" data-testid={`archive-account-${a.id}`}>
                                            <Archive className="h-4 w-4 text-muted-foreground" />
                                        </Button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
}
